import type { PossibleDate } from "./parseDate";

import { parseDate } from "./parseDate";

const getDefaultOptions = (
  format: "date" | "date-time" | "time",
): Intl.DateTimeFormatOptions => {
  return {
    ...(format === "date" && {
      timeZone: "UTC",
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    }),
    ...(format === "time" && {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }),
    ...(format === "date-time" && {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }),
  };
};

const ordinalSuffixes: Record<string, string> = {
  one: "st",
  two: "nd",
  few: "rd",
  other: "th",
};

const getOrdinalSuffix = (
  day: number,
  locale: Intl.LocalesArgument,
): string => {
  if (typeof day !== "number" || day < 1) {
    return "";
  }
  const ordinalPluralRules = new Intl.PluralRules(locale, { type: "ordinal" });
  const ordinal = ordinalPluralRules.select(day);
  return ordinalSuffixes[ordinal] ?? "th";
};

const formatSingleDate = (
  date: PossibleDate,
  formatter: Intl.DateTimeFormat,
  ordinalDay: boolean,
  locale: Intl.LocalesArgument,
): string | null => {
  const parseDateResult = parseDate(date);
  if (!parseDateResult.success) return null;

  if (ordinalDay) {
    const parts = formatter.formatToParts(parseDateResult.data);
    const dayPart = parts.find((p) => p.type === "day");

    if (dayPart) {
      const day = Number.parseInt(dayPart.value, 10);
      const suffix = getOrdinalSuffix(day, locale);
      return parts
        .map((part) => {
          if (part.type === "day") {
            return day + suffix;
          }
          return part.value;
        })
        .join("");
    }
  }

  return formatter.format(parseDateResult.data);
};

interface FormatDateOptions {
  date: PossibleDate | [PossibleDate, PossibleDate];
  format?: "date" | "date-time" | "time";
  options?: Intl.DateTimeFormatOptions;
  locale?: Intl.LocalesArgument;
  ordinalDay?: boolean;
}

export function formatDate({
  date,
  format = "date",
  options,
  locale = "en-US",
  ordinalDay = false,
}: FormatDateOptions): string {
  const defaultOptions = getDefaultOptions(format);
  const mergedOptions = { ...defaultOptions, ...options };
  const formatter = new Intl.DateTimeFormat(locale, mergedOptions);

  if (Array.isArray(date)) {
    const [start, end] = date;
    const startParsed = parseDate(start);
    const endParsed = parseDate(end);

    if (!startParsed.success && !endParsed.success) return "Invalid date";
    if (!startParsed.success)
      return (
        formatSingleDate(end, formatter, ordinalDay, locale) ?? "Invalid date"
      );
    if (!endParsed.success)
      return (
        formatSingleDate(start, formatter, ordinalDay, locale) ?? "Invalid date"
      );

    return formatter.formatRange(startParsed.data, endParsed.data);
  }

  const result = formatSingleDate(date, formatter, ordinalDay, locale);
  return result ?? "Invalid date";
}
